import React from 'react'
import * as zarr from 'zarrita'
import type { Dataset, ControlsProps } from './types'
import { BandSelector } from '../components/SharedControls'

// S1 GRD product — inner group path within the zarr store
const S1_ZARR = 'S1A_IW_GRDH_1SDV_20240201T164915_20240201T164940_052368_065517_750E.zarr'
const S1_INNER = 'S01SIWGRD_20240201T164915_0025_A299_750E_065517'
const PROXY_BASE = `/eopf-examples/${S1_ZARR}/${S1_INNER}`

// 4 multiscale levels: r2, r4, r6, r8
const LEVELS = ['r2', 'r4', 'r6', 'r8']

// Custom store that intercepts root zarr.json to inject the multiscales layout
// (the real root has no multiscales attr; zarr-layer needs it to find the pyramid)
class EopfS1GrdStore {
  async get(key: string): Promise<Uint8Array | undefined> {
    const k = key.startsWith('/') ? key.slice(1) : key
    const resp = await fetch(`${PROXY_BASE}/${k}`)
    if (!resp.ok) return undefined
    if (k === 'zarr.json') {
      const meta = await resp.json()
      meta.attributes = {
        ...meta.attributes,
        multiscales: {
          layout: LEVELS.map((l) => ({ asset: `multiscales/${l}` })),
        },
      }
      return new TextEncoder().encode(JSON.stringify(meta))
    }
    return new Uint8Array(await resp.arrayBuffer())
  }

  async getRange(
    key: string,
    range: { offset?: number; length?: number; suffixLength?: number },
    options?: { signal?: AbortSignal }
  ): Promise<Uint8Array | undefined> {
    const k = key.startsWith('/') ? key.slice(1) : key
    const rangeHeader =
      range.suffixLength !== undefined
        ? `bytes=-${range.suffixLength}`
        : `bytes=${range.offset}-${(range.offset ?? 0) + (range.length ?? 0) - 1}`
    const resp = await fetch(`${PROXY_BASE}/${k}`, {
      headers: { Range: rangeHeader },
      signal: options?.signal,
    })
    if (!resp.ok) return undefined
    return new Uint8Array(await resp.arrayBuffer())
  }
}

type Polarization = 'VV' | 'VH'
type S1GrdState = { polarization: Polarization }

const Controls = ({ state, setState }: ControlsProps<S1GrdState>) => (
  <BandSelector
    value={state.polarization}
    options={['VV', 'VH'] as Polarization[]}
    onChange={(polarization) => setState({ polarization })}
    label="Polarization"
  />
)

const eopfS1Grd: Dataset<S1GrdState> = {
  id: 'eopf-s1-grd',
  tags: ['sentinel-1', 'sar', 'italy'],
  source: PROXY_BASE,
  variable: 'grd',
  clim: [17, 156],
  colormap: 'greys',
  zarrVersion: 3,
  // EPSG:4326 — no proj4 transform needed
  spatialDimensions: { lat: 'y', lon: 'x' },
  bounds: [15.216035, 39.456672, 18.623531, 41.367186],
  latIsAscending: false,
  fillValue: 65535,
  center: [16.9, 40.4],
  zoom: 7,
  info: 'Sentinel-1 GRD IW',
  subtitle: 'Italy · EPSG:4326 · ~47 m · 2024-02-01',
  sourceInfo:
    'Sentinel-1 IW GRD product (EOPF zarr v3) covering a scene over southern Italy, acquired 2024-02-01.',
  store: Promise.resolve(new EopfS1GrdStore() as zarr.Readable),
  defaultState: { polarization: 'VV' },
  Controls,
  buildLayerProps: (state) => ({
    variable: 'grd',
    selector: {
      polarization: { selected: state.polarization === 'VV' ? 0 : 1, type: 'index' },
    },
  }),
}

export default eopfS1Grd
